/**
 * Icon migration utilities for attack path data.
 *
 * Handles migration from legacy icon identifiers (e.g., 'Server')
 * to new prefixed format (e.g., 'lucide:server').
 *
 * @module iconMigration
 */

import type { Edge } from '@xyflow/react';

import type { AttackStage } from '@/sections/attacks/attackPaths/types';

/** Current schema version for icon identifiers */
export const CURRENT_ICON_VERSION = 2;

/** Previous schema version (legacy Lucide icon names) */
export const LEGACY_ICON_VERSION = 1;

/**
 * Generic node type for migration purposes.
 * More flexible than CustomNode to handle migration of any node with data.
 */
interface MigrationNode {
  id: string;
  type?: string;
  position?: { x: number; y: number };
  data: {
    id?: string;
    name?: string;
    iconOverride?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Attack path data with version tracking
 */
export interface VersionedAttackPath {
  /** Schema version for migration tracking */
  version?: number;
  /** Attack path nodes */
  nodes: MigrationNode[];
  /** Attack path edges */
  edges: Edge[];
  /** Persisted attack stages (optional, auto-generated from technique nodes if absent) */
  stages?: AttackStage[];
}

/**
 * Mapping from legacy icon names to new prefixed format.
 *
 * Migration strategy:
 * - Infrastructure icons (Computer, Server, Network, Router, Database, Firewall) → infrastructure: prefix
 * - Cloud/Identity/App icons → lucide: prefix with PascalCase names
 * - AD-specific icons (DomainController, CertAuthority) → ad: prefix for specialized display
 * - Globe (never existed) → lucide:WebApp (closest equivalent)
 */
const legacyIconMap: Record<string, string> = {
  // Infrastructure → infrastructure: prefix (not lucide:)
  Computer: 'infrastructure:Computer',
  Server: 'infrastructure:Server',
  Network: 'infrastructure:Network',
  Router: 'infrastructure:Router',
  Database: 'infrastructure:Database',

  // Globe never existed, map to closest equivalent
  Globe: 'lucide:WebApp', // WebApp is the closest internet/domain icon

  // Cloud → Use actual LUCIDE_ICONS (PascalCase, not lowercase)
  Cloud: 'lucide:Cloud',
  Compute: 'lucide:Compute',
  Serverless: 'lucide:Serverless',
  Storage: 'lucide:Storage',
  Container: 'lucide:Container',

  // Identity → Use actual LUCIDE_ICONS
  User: 'lucide:User',
  Group: 'lucide:Group',
  Principal: 'lucide:Principal',

  // AD-specific icons stay with ad: prefix
  DomainController: 'ad:DomainController',
  CertAuthority: 'ad:CertificateAuthority',

  // Applications → Use actual LUCIDE_ICONS
  WebApp: 'lucide:WebApp',
  Bot: 'lucide:Bot',

  // Security → infrastructure: prefix for Firewall
  Firewall: 'infrastructure:Firewall', // Changed from lucide:shield
  CrownJewel: 'lucide:CrownJewel',

  // Default (empty string = use default icon)
  '': '',
};

/**
 * Reverse mapping for rollback operations
 */
const reverseIconMap: Record<string, string> = Object.fromEntries(
  Object.entries(legacyIconMap)
    .filter(([_, v]) => v !== '')
    .map(([k, v]) => [v, k])
);

/**
 * Migrates a legacy icon identifier to the new prefixed format.
 *
 * @param iconOverride - Legacy icon identifier (e.g., 'Server') or undefined
 * @returns New prefixed identifier (e.g., 'lucide:server') or original if already migrated
 *
 * @example
 * ```typescript
 * migrateLegacyIcon('Server');     // Returns 'lucide:server'
 * migrateLegacyIcon('aws:Lambda'); // Returns 'aws:Lambda' (already migrated)
 * migrateLegacyIcon(undefined);    // Returns undefined
 * migrateLegacyIcon('');           // Returns '' (default icon)
 * ```
 */
export function migrateLegacyIcon(
  iconOverride: string | undefined
): string | undefined {
  // Undefined stays undefined
  if (iconOverride === undefined) return undefined;

  // Empty string stays empty (means "use default")
  if (iconOverride === '') return '';

  // Already migrated (has prefix)
  if (iconOverride.includes(':')) return iconOverride;

  // Case-insensitive lookup for legacy icons
  const normalizedKey = Object.keys(legacyIconMap).find(
    k => k.toLowerCase() === iconOverride.toLowerCase()
  );

  if (normalizedKey !== undefined) {
    return legacyIconMap[normalizedKey];
  }

  // Unknown icon - log warning and use fallback
  console.warn(
    `[iconMigration] Unknown legacy icon: "${iconOverride}", using fallback`
  );
  return 'lucide:helpCircle';
}

/**
 * Rolls back a new icon identifier to the legacy format.
 * Used for emergency rollback scenarios.
 *
 * @param iconOverride - New prefixed identifier (e.g., 'lucide:server')
 * @returns Legacy identifier (e.g., 'Server') or original if not recognized
 */
export function rollbackIcon(
  iconOverride: string | undefined
): string | undefined {
  if (iconOverride === undefined) return undefined;
  if (iconOverride === '') return '';
  if (!iconOverride.includes(':')) return iconOverride; // Already legacy format

  return reverseIconMap[iconOverride] ?? iconOverride;
}

/**
 * Migrates an entire attack path's icons to the new format.
 *
 * @param data - Attack path data with nodes and edges
 * @returns Migrated attack path with updated version
 *
 * @example
 * ```typescript
 * const migratedPath = migrateAttackPath({
 *   nodes: [{ data: { iconOverride: 'Server' } }],
 *   edges: []
 * });
 * // migratedPath.version === 2
 * // migratedPath.nodes[0].data.iconOverride === 'lucide:server'
 * ```
 */
export function migrateAttackPath(
  data: VersionedAttackPath
): VersionedAttackPath {
  const currentVersion = data.version ?? LEGACY_ICON_VERSION;

  // Already at current version or higher
  if (currentVersion >= CURRENT_ICON_VERSION) {
    return data;
  }

  // Version 1 -> 2: Migrate legacy Lucide icons to prefixed format
  if (currentVersion < 2) {
    return {
      ...data,
      version: CURRENT_ICON_VERSION,
      nodes: data.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          iconOverride: migrateLegacyIcon(node.data.iconOverride),
        },
      })),
    };
  }

  return data;
}

/**
 * Rolls back an attack path to the legacy format.
 * Used for emergency rollback scenarios.
 *
 * @param data - Attack path data with migrated icons
 * @returns Attack path with legacy icon format
 */
export function rollbackAttackPath(
  data: VersionedAttackPath
): VersionedAttackPath {
  return {
    ...data,
    version: LEGACY_ICON_VERSION,
    nodes: data.nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        iconOverride: rollbackIcon(node.data.iconOverride),
      },
    })),
  };
}

/**
 * Checks if an attack path needs migration.
 *
 * @param data - Attack path data
 * @returns true if migration is needed
 */
export function needsMigration(data: VersionedAttackPath): boolean {
  return (data.version ?? LEGACY_ICON_VERSION) < CURRENT_ICON_VERSION;
}

/**
 * Gets all unique legacy icons that need migration in an attack path.
 * Useful for migration reports.
 *
 * @param data - Attack path data
 * @returns Array of legacy icon identifiers found
 */
export function getLegacyIcons(data: VersionedAttackPath): string[] {
  const legacyIcons = new Set<string>();

  for (const node of data.nodes) {
    const icon = node.data.iconOverride;
    if (icon && !icon.includes(':') && icon !== '') {
      legacyIcons.add(icon);
    }
  }

  return Array.from(legacyIcons);
}
